<script>
  import Layout from "$app/layouts/Main.svelte"
  import { CircleCheckBig, CircleQuestionMark } from "lucide-svelte"

  let props = $props()

  import logo from "@images/fast-attack-logo.png"
  import submarine from "@images/submarine.png"
  import map from "@images/map.jpg"
  import start_line from "@images/start-line.jpg"
</script>

<Layout {...props}>
  <div class="relative isolate overflow-hidden bg-gray-900">
    <svg
      class="absolute inset-0 -z-10 size-full mask-[radial-gradient(100%_100%_at_top_right,white,transparent)] stroke-white/10"
      aria-hidden="true"
    >
      <defs>
        <pattern
          id="983e3e4c-de6d-4c3f-8d64-b9761d1534cc"
          width="200"
          height="200"
          x="50%"
          y="-1"
          patternUnits="userSpaceOnUse"
        >
          <path d="M.5 200V.5H200" fill="none" />
        </pattern>
      </defs>
      <svg x="50%" y="-1" class="overflow-visible fill-gray-800/20">
        <path
          d="M-200 0h201v201h-201Z M600 0h201v201h-201Z M-400 600h201v201h-201Z M200 800h201v201h-201Z"
          stroke-width="0"
        />
      </svg>
      <rect
        width="100%"
        height="100%"
        stroke-width="0"
        fill="url(#983e3e4c-de6d-4c3f-8d64-b9761d1534cc)"
      />
    </svg>
    <div
      class="absolute top-10 left-[calc(50%-4rem)] -z-10 transform-gpu blur-3xl sm:left-[calc(50%-18rem)] lg:top-[calc(50%-30rem)] lg:left-48 xl:left-[calc(50%-24rem)]"
      aria-hidden="true"
    >
      <div
        class="aspect-1108/632 w-277 bg-linear-to-r from-[#80caff] to-[#4f46e5] opacity-20"
        style="clip-path: polygon(73.6% 51.7%, 91.7% 11.8%, 100% 46.4%, 97.4% 82.2%, 92.5% 84.9%, 75.7% 64%, 55.3% 47.5%, 46.5% 49.4%, 45% 62.9%, 50.3% 87.2%, 21.3% 64.1%, 0.1% 100%, 5.4% 51.1%, 21.4% 63.9%, 58.9% 0.2%, 73.6% 51.7%)"
      ></div>
    </div>
    <div
      class="mx-auto max-w-7xl px-6 pt-10 pb-24 sm:pb-32 lg:flex lg:px-8 lg:py-40"
    >
      <div class="mx-auto max-w-2xl shrink-0 lg:mx-0 lg:pt-8">
        <img class="h-18 sm:h-24 mx-auto" src={logo} alt="Your Company" />

        <h1
          class="mt-6 sm:mt-10 text-4xl font-semibold tracking-loose text-pretty text-white sm:text-7xl text-center font-oxygen"
        >
          "How Ready Are You, Really?"
        </h1>
        <p
          class="mt-8 sm:mt-12 text-base text-center sm:text-left font-light text-gray-300 sm:text-xl/8 font-merriweather"
        >
          A Readiness Score Quiz for Today's Leaders
        </p>

        <p
          class="mt-6 text-sm text-center sm:text-left font-light italic text-gray-300 sm:text-xl/8 font-merriweather"
        >
          Based on real moments. Answer with radical honesty.
        </p>

        <div
          class="mt-10 flex items-center justify-center sm:justify-start gap-x-6"
        >
          <form method="post" action="/quiz">
            <input
              type="submit"
              class="rounded-md bg-indigo-500 px-3.5 py-2.5 text-xl font-semibold text-white shadow-xs hover:bg-indigo-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-400"
              value="Take the Quiz"
            />
          </form>
        </div>
      </div>
      <div
        class="mx-auto mt-10 sm:mt-16 flex max-w-2xl sm:mt-24 lg:mt-0 lg:mr-0 lg:ml-10 lg:max-w-none lg:flex-none xl:ml-32"
      >
        <div class="max-w-3xl flex-none sm:max-w-5xl lg:max-w-none">
          <img
            src={submarine}
            alt="App screenshot"
            class="w-84 sm:w-204 rounded-md bg-white/5 shadow-2xl ring-1 ring-white/10"
          />
        </div>
      </div>
    </div>
  </div>

  <div class="overflow-hidden bg-white py-24 sm:py-32">
    <div class="mx-auto max-w-7xl md:px-6 lg:px-8">
      <div
        class="grid grid-cols-1 gap-x-8 gap-y-16 sm:gap-y-20 lg:grid-cols-2 lg:items-start"
      >
        <div class="px-6 lg:px-0 lg:pt-4 lg:pr-4">
          <div class="mx-auto max-w-2xl lg:mx-0 lg:max-w-lg">
            <p
              class="mt-2 text-3xl font-semibold tracking-tight text-pretty text-gray-900 sm:text-5xl"
            >
              How Ready Is Your Leadership Team to Navigate Disruption?
            </p>
            <p class="mt-6 text-lg/8 text-gray-600">
              Discover your blind spots and what you can do about them by taking
              the
              <span class="font-bold">FREE</span> Lead With Purpose Readiness Quiz.
              Developed from leadership and alignment frameworks born out of the
              US Nuclear Submarine Force.
            </p>
            <dl
              class="mt-10 max-w-xl space-y-8 text-base/7 text-gray-600 lg:max-w-none"
            >
              <div class="relative pl-9">
                <dt class="inline font-semibold text-gray-900">
                  <CircleQuestionMark
                    class="absolute top-1 left-1 size-5 text-blue-600"
                  />
                  Are you struggling to manage the pace of change?
                </dt>
                <dd class="inline">
                  The hard truth is that the pace of change is only accelerating
                  and if you are not preparing your team to navigate these
                  uncertain times you will get left behind.
                </dd>
              </div>
              <div class="relative pl-9">
                <dt class="inline font-semibold text-gray-900">
                  <CircleQuestionMark
                    class="absolute top-1 left-1 size-5 text-blue-600"
                  />
                  Are you confident that your team is 100% unified and aligned?
                </dt>
                <dd class="inline">
                  Can you afford to simply keep coasting and hoping that
                  everything will work out? You need to know with certainty how
                  aligned your team really is.
                </dd>
              </div>
            </dl>
          </div>
        </div>
        <div class="sm:px-6 lg:px-0">
          <div class="mx-auto max-w-2xl sm:mx-0 sm:max-w-none">
            <img
              width="2432"
              height="1442"
              src={start_line}
              alt="Ready to launch"
              class="-mb-12 w-228 max-w-none rounded-tl-xl bg-gray-800 ring ring-white/10"
            />
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="relative isolate overflow-hidden bg-gray-900">
    <div class="px-6 py-24 sm:px-6 sm:py-32 lg:px-8">
      <div class="mx-auto max-w-2xl text-center">
        <h2
          class="text-3xl font-semibold tracking-tight text-balance text-white sm:text-5xl"
        >
          Take the Readiness Quiz Today
        </h2>
        <p
          class="mx-auto mt-6 max-w-2xl text-sm md:text-base/8 text-pretty text-gray-300 italic"
        >
          Don't keep simply hoping that your team is ready to navigate the
          coming storms.
        </p>
        <p
          class="mx-auto mt-6 max-w-xl text-base md:text-lg/8 text-pretty text-gray-200"
        >
          Take our quiz, which is based on real-world situations, and discover
          the actions you can take today to <span class="font-bold"
            >build resiliance, agility and prepare to thrive in the coming
            disruptions.</span
          >
        </p>
        <div class="mt-10 flex items-center justify-center gap-x-6">
          <form method="post" action="/quiz">
            <input
              type="submit"
              class="rounded-md bg-white/15 px-3.5 py-2.5 text-sm font-semibold text-white inset-ring inset-ring-white/5 hover:bg-white/20 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              value="Take the Quiz"
            />
          </form>
        </div>
      </div>
    </div>
    <svg
      viewBox="0 0 1024 1024"
      aria-hidden="true"
      class="absolute top-1/2 left-1/2 -z-10 size-256 -translate-x-1/2 mask-[radial-gradient(closest-side,white,transparent)]"
    >
      <circle
        r="512"
        cx="512"
        cy="512"
        fill="url(#8d958450-c69f-4251-94bc-4e091a323369)"
        fill-opacity="0.7"
      />
      <defs>
        <radialGradient id="8d958450-c69f-4251-94bc-4e091a323369">
          <stop stop-color="#7775D6" />
          <stop offset="1" stop-color="#E935C1" />
        </radialGradient>
      </defs>
    </svg>
  </div>
</Layout>
